import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  PageHeader,
  DetailCard,
  ProductSearchInput,
  BarcodeScannerModal,
} from '../components';
import { useCreateReservation } from '../hooks/useStockReservations';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors } from '../constants';

const emptyItem = () => ({
  sku: '',
  productUuid: '',
  productType: 'product',
  quantity: '',
  selectedItem: null,
});

export default function StockReservationCreate() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const create = useCreateReservation();

  const [orderUuid, setOrderUuid] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const [error, setError] = useState(null);

  const updateItem = (idx, updates) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...updates } : it)));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleItemSelected = (idx, item) =>
    updateItem(idx, {
      sku: item.sku,
      productUuid: item.uuid,
      productType: item.type,
      selectedItem: item,
    });

  const handleBarcodeFound = (item) => {
    if (scanTarget !== null && scanTarget < items.length) {
      handleItemSelected(scanTarget, item);
    } else {
      setItems((prev) => [
        ...prev,
        { ...emptyItem(), sku: item.sku, productUuid: item.uuid, productType: item.type, selectedItem: item },
      ]);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!orderUuid.trim() || !orderRef.trim())
      return setError('Order UUID and reference are required.');
    if (items.length === 0) return setError('Add at least one item to reserve.');
    for (let i = 0; i < items.length; i += 1) {
      if (!items[i].sku?.trim()) return setError(`Line ${i + 1}: select a product.`);
      const qty = parseFloat(items[i].quantity);
      if (!Number.isFinite(qty) || qty <= 0)
        return setError(`Line ${i + 1}: quantity must be greater than zero.`);
    }
    try {
      await create.mutateAsync({
        businessUuid,
        orderUuid,
        orderReference: orderRef,
        notes: notes || undefined,
        items: items.map((i) => ({
          productUuid: i.productUuid || i.sku,
          productType: i.productType,
          sku: i.sku,
          quantity: parseFloat(i.quantity) || 0,
        })),
      });
      history.push('/inventory-management/stock-reservations');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
    return undefined;
  };

  return (
    <>
      <PageHeader title="New Reservation" backTo="/inventory-management/stock-reservations" />

      {error && (
        <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard title="Reservation">
            <Stack spacing={2}>
              <TextField label="Order UUID" value={orderUuid} onChange={(e) => setOrderUuid(e.target.value)} fullWidth required size="small" />
              <TextField label="Order Reference" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} fullWidth required size="small" placeholder="ORD-001" />
              <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} size="small" />
            </Stack>
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard
            title={`Items ${items.length ? `(${items.length})` : ''}`}
            action={
              <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addItem} sx={{ textTransform: 'none' }}>
                Add item
              </Button>
            }
          >
            {items.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.gray400, py: 3, textAlign: 'center' }}>
                No items to reserve. Tap "Add item" to begin.
              </Typography>
            ) : (
              items.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{ p: 2, mb: 1.5, borderRadius: 2, border: `1px solid ${colors.border}`, backgroundColor: colors.bg }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: colors.gray600 }}>
                      Item {idx + 1}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <ProductSearchInput
                    value={item.sku}
                    onChangeText={(v) => updateItem(idx, { sku: v, productUuid: '', selectedItem: null })}
                    onItemSelected={(found) => handleItemSelected(idx, found)}
                    productType="product"
                    selectedItem={item.selectedItem}
                    onClear={() => updateItem(idx, { sku: '', productUuid: '', selectedItem: null })}
                    onBarcodeScan={() => {
                      setScanTarget(idx);
                      setScannerOpen(true);
                    }}
                  />
                  <TextField
                    label="Quantity"
                    type="number"
                    required
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    helperText={item.selectedItem ? `Available: ${item.selectedItem.stock} ${item.selectedItem.unit}` : ' '}
                  />
                </Box>
              ))
            )}
          </DetailCard>

          <Box sx={{ mt: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={create.isPending || items.length === 0}
              sx={{ backgroundColor: colors.primary, textTransform: 'none', fontWeight: 600, py: 1.25 }}
            >
              Create Reservation
            </Button>
          </Box>
        </Grid>
      </Grid>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeFound}
        onBarcodeNotFound={(code) => setError(`No product found for barcode "${code}".`)}
      />
    </>
  );
}
